import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Workshops.css';

// Function to format date and time together
const formatDateTime = (dateTimeStr, t) => {
  if (!dateTimeStr) return '';
  
  try {
    const date = new Date(dateTimeStr);
    const locale = t('date_locale') || 'en-US';
    
    const formattedDate = date.toLocaleDateString(locale, { 
      day: 'numeric',
      month: 'numeric'
    });
    
    const formattedTime = date.toLocaleTimeString(locale, { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
    
    // Use simple string concatenation with the translated connector
    return `${formattedDate} ${t('workshop_section.date_time_connector')} ${formattedTime}`;
  } catch (e) {
    // console.error("Error formatting date and time:", e);
    return '';
  }
};

// Function to get difficulty level text
const getDifficultyText = (difficulty, t) => {
  if (!difficulty && difficulty !== 0) return t('workshop_section.difficulty_all_levels');
  
  // Convert to number if it's a string
  const diffLevel = typeof difficulty === 'string' ? parseInt(difficulty, 10) : difficulty;
  
  switch (diffLevel) {
    case 1: return t('workshop_section.difficulty_beginner');
    case 2: return t('workshop_section.difficulty_intermediate');
    case 3: return t('workshop_section.difficulty_advanced');
    default: return t('workshop_section.difficulty_all_levels');
  }
};

// Function to get difficulty class
const getDifficultyClass = (difficulty) => {
  if (!difficulty && difficulty !== 0) return 'difficulty-all-levels';
  
  // Convert to number if it's a string
  const diffLevel = typeof difficulty === 'string' ? parseInt(difficulty, 10) : difficulty;
  
  switch (diffLevel) {
    case 1: return 'difficulty-beginner';
    case 2: return 'difficulty-intermediate';
    case 3: return 'difficulty-advanced';
    default: return 'difficulty-all-levels';
  }
};

const WorkshopCard = ({ workshop }) => {
  const { t, i18n } = useTranslation();
  const [workshopImage, setWorkshopImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { currentTheme } = useTheme();
  
  // Language detection
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';
  
  const {
    id,
    title,
    class_type,
    start_time,
    duration,
    instructor,
    instructor_id,
    instructor_title,
    instructor_image,
    location,
    difficulty,
    available_spots,
    max_capacity,
    price,
    image_url
  } = workshop;

  // Add this new state to track expanded/collapsed state
  const [isExpanded, setIsExpanded] = useState(false);

  // Add these event handlers
  const handleInteraction = () => {
    setIsExpanded(true);
  };

  const handleTouchStart = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  // Fetch images for the workshop
  useEffect(() => {
    if (!id) return;
    
    setIsLoading(true);
    axios.get(`classes/${id}/images`)
      .then(response => {
        // console.log('Fetched workshop images:', response.data);
        
        if (response.data.images && Array.isArray(response.data.images) && response.data.images.length > 0) {
          setWorkshopImage(response.data.images[0]);
        } else if (image_url) {
          setWorkshopImage(image_url);
        }
      })
      .catch(error => {
        // console.error('Error fetching workshop images:', error);
        if (image_url) {
          setWorkshopImage(image_url);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, image_url]);

  // Default image if none provided
  const defaultImage = 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&w=500&q=60';
  const imageToDisplay = workshopImage || image_url || defaultImage;

  return (
    <div 
      className={`themed-workshop-container ${isExpanded ? 'expanded' : ''}`}
      onMouseEnter={handleInteraction}
      onTouchStart={handleTouchStart}
      style={{
        backgroundImage: !isExpanded ? `url(${imageToDisplay})` : 'none',
      }}
      lang={currentLanguage}
      itemScope
      itemType="https://schema.org/Event"
    >
      <meta itemProp="name" content={title} />
      <meta itemProp="startDate" content={start_time} />
      {location && <meta itemProp="location" content={location} />}
      {price !== undefined && (
        <meta itemProp="offers" content={JSON.stringify({
          "@type": "Offer",
          "price": price,
          "priceCurrency": "EUR",
          "availability": available_spots > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut"
        })} />
      )}
      
      <div className="workshop-card">
        {/* Top circle - Workshop image */}
        <div className="workshop-image-circle">
          {isLoading ? (
            <div className="image-loading" lang={currentLanguage}>{t('loader.loading')}</div>
          ) : (
            <img src={imageToDisplay} alt={title} itemProp="image" />
          )}
        </div>
        
        {/* Middle ellipse - Workshop info */}
        <div className="workshop-info-ellipse">
          <h3 className="workshop-title" itemProp="name">{title}</h3>
          
          <div className="workshop-date-time">
            <div className="workshop-detail">
              <i className="fa fa-calendar" aria-hidden="true"></i>
              <span itemProp="startDate">{formatDateTime(start_time, t)}</span>
            </div>
            
            <div className="workshop-detail">
              <i className="fa fa-hourglass-half" aria-hidden="true"></i>
              <span itemProp="duration">{duration} {t("workshop.minutes", "minutes")}</span>
            </div>
            <Link 
              to={`/workshops/${id}`} 
              className="learn-more-button" 
              lang={currentLanguage}
              title={isSlovak ? `Zobrazi� podrobnosti o workshope: ${title}` : `View workshop details: ${title}`}
            >
              {t('workshop_section.details')}
            </Link>
          </div>
        </div>
        
        {/* Bottom circle - Instructor & details */}
        <div className="workshop-details-circle">
          <div className="workshop-instructor" itemScope itemType="https://schema.org/Person" itemProp="performer">
            {instructor_image && (
              <div className="instructor-avatar">
                <img src={instructor_image} alt={instructor} itemProp="image" />
              </div>
            )}
            
            <Link 
              to={`/instructors/${instructor_id}`} 
              className="instructor-name" 
              lang={currentLanguage}
              itemProp="name"
            >
              {instructor}
            </Link>
            
            {instructor_title && (
              <span className="instructor-title" itemProp="jobTitle">{instructor_title}</span>
            )}
          </div>
          
          {/* ��� Donut Progress Section ��� */}
          <div className="workshop-availability" itemProp="offers" itemScope itemType="https://schema.org/Offer">
            <meta itemProp="price" content={price !== undefined ? price : 0} />
            <meta itemProp="priceCurrency" content="EUR" />
            <meta itemProp="availability" content={available_spots > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut"} />
            
            <div
              className="circular-availability"
              style={{
                '--pct': (available_spots / max_capacity) * 100
              }}
              aria-label={isSlovak ? `${available_spots} vo�n�ch miest z ${max_capacity}` : `${available_spots} spots available out of ${max_capacity}`}
            >
              {available_spots}
            </div>
            <div className="availability-text" lang={currentLanguage}>
              {t('workshop_details.spots_available')}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default WorkshopCard;
